import { motion } from "framer-motion";
import { Zap } from "lucide-react";
import { EXPECTATIONS, HIGHLIGHTS, PDF_BG } from "../data/content";
import SectionBg from "./SectionBg";

export default function Highlights() {
    return (
        <>
            {/* Expectations */}
            <section
                id="expectations"
                className="relative py-24 md:py-32 overflow-hidden"
                data-testid="expectations-section"
            >
                <SectionBg src={PDF_BG.expectations} opacity={0.6} />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="grid grid-cols-12 gap-6 md:gap-12 mb-10 md:mb-12">
                        <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                            What You Walk Away With
                        </div>
                        <h2 className="col-span-12 md:col-span-8 min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                            Expect <span className="gradient-text">more</span>. Then expect more again.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {EXPECTATIONS.map((e, i) => (
                            <motion.div
                                key={e}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ delay: i * 0.06 }}
                                className="group flex items-start gap-3 sm:gap-4 p-5 sm:p-6 rounded-2xl glass-light hover:border-[#FF6B35]/50 transition-all"
                                data-testid={`expectation-${i}`}
                            >
                                <Zap
                                    size={20}
                                    className="flex-none text-[#FF6B35] mt-1 group-hover:scale-125 transition"
                                />
                                <span className="min-w-0 text-sm sm:text-base text-zinc-200 leading-relaxed [overflow-wrap:anywhere]">{e}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Highlights bento */}
            <section
                id="highlights"
                className="relative py-24 md:py-40 overflow-hidden"
                data-testid="highlights-section"
            >
                <SectionBg src={PDF_BG.highlights} opacity={0.6} />
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(157,76,221,0.12)_0%,_transparent_60%)]" />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12 md:mb-16">
                        <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                            By The Numbers
                        </div>
                        <h2 className="col-span-12 md:col-span-8 min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                            JAF 2026 in <span className="text-stroke-orange">numbers</span>.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                        {HIGHLIGHTS.map((h, i) => (
                            <motion.div
                                key={h.label}
                                initial={{ opacity: 0, scale: 0.92, y: 30 }}
                                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ delay: i * 0.08, duration: 0.7 }}
                                whileHover={{ y: -6 }}
                                className="group relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#141414] to-[#0a0a0a] p-6 sm:p-8 md:p-10 overflow-hidden hover:border-[#FF6B35]/60 transition-all duration-500"
                                data-testid={`highlight-${i}`}
                            >
                                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#FF6B35]/0 group-hover:bg-[#FF6B35]/20 blur-3xl transition-all duration-700" />
                                <div className="relative">
                                    <div className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter gradient-text">
                                        {h.value}
                                    </div>
                                    <div className="mt-4 text-xs uppercase tracking-[0.25em] text-zinc-400 font-mono">
                                        {h.label}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
